// Lee un rincón mientras su pestaña está abierta y lo vuelve a pedir cada 30 s (el servidor lo recalcula en
// segundo plano en cuanto llega dato nuevo; pedirlo nunca espera al almacén de datos y no consulta nada).
// 22-10 (revisión de la sala): la primera petición despierta el cálculo en el servidor, que tarda de unos
// segundos a casi un minuto. Mientras no hay ni datos, ni error, ni cálculo hecho, el rincón está
// «calculando» (nunca «sin datos») y se vuelve a pedir cada pocos segundos para enseñarlo en cuanto esté.
import { useCallback, useEffect, useState } from 'react';
import { salaApi } from './api';
import type { FotoRinconSala, NombreFotoSala } from './tipos';

const MIENTRAS_CALCULA_MS = 3_000;

/** El servidor aún no ha terminado de calcular este rincón ni una vez (y no ha fallado). */
export function calculandoPrimeraVez(foto: FotoRinconSala<unknown> | null): boolean {
  return foto === null || (foto.datos === null && foto.error === null && foto.generado_at === null);
}

/** Tras «Actualizar ya», se vuelve a pedir a los pocos segundos (el servidor recalcula en segundo plano). */
const TRAS_PEDIR_YA_MS = 4_000;

export function useSalaRincon<T>(
  nombre: NombreFotoSala,
  cadaMs = 30_000,
): { foto: FotoRinconSala<T> | null; fallo: boolean; actualizarYa: () => void; actualizando: boolean } {
  const [foto, setFoto] = useState<FotoRinconSala<T> | null>(null);
  const [fallo, setFallo] = useState(false);
  const [pedidoYa, setPedidoYa] = useState(0);
  const [actualizando, setActualizando] = useState(false);
  useEffect(() => {
    let vivo = true;
    let espera: ReturnType<typeof setTimeout> | undefined;
    if (pedidoYa === 0) setFoto(null);
    let ya = pedidoYa > 0;
    const pedir = () =>
      salaApi
        .rincon<T>(nombre, ya)
        .then((f) => {
          if (!vivo) return;
          setFoto(f);
          setFallo(false);
          return f;
        })
        .catch(() => {
          if (vivo) setFallo(true);
          return null;
        })
        .then((f) => {
          if (!vivo) return;
          const recienPedido = ya;
          ya = false;
          if (!recienPedido) setActualizando(false);
          espera = setTimeout(pedir, recienPedido ? TRAS_PEDIR_YA_MS : f && calculandoPrimeraVez(f) ? MIENTRAS_CALCULA_MS : cadaMs);
        });
    void pedir();
    return () => {
      vivo = false;
      clearTimeout(espera);
    };
  }, [nombre, cadaMs, pedidoYa]);
  const actualizarYa = useCallback(() => {
    setActualizando(true);
    setPedidoYa((n) => n + 1);
  }, []);
  return { foto, fallo, actualizarYa, actualizando };
}

/** Al abrir la sala, despierta el cálculo de todos los rincones a la vez: cuando Pau entra en una pestaña, ya está. */
export function despiertaRincones(nombres: readonly NombreFotoSala[]): void {
  for (const n of nombres) void salaApi.rincon(n).catch(() => undefined);
}
